import React, { useEffect, useMemo, useState } from 'react';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import "../css/game.css"

const LEVEL_COUNT = 4;

// --- Helpers for tree navigation ---

function findNodeById(node, id) {
    if (!node) return null;
    if (node.id === id) return node;
    if (!node.children) return null;
    for (const child of node.children) {
        const found = findNodeById(child, id);
        if (found) return found;
    }
    return null;
}

function findFolderById(root, id) {
    const node = findNodeById(root, id);
    return node && node.type === 'folder' ? node : null;
}

function getChildFolders(folder) {
    if (!folder || !folder.children) return [];
    return folder.children.filter((c) => c.type === 'folder');
}

function getChildCards(folder) {
    if (!folder || !folder.children) return [];
    return folder.children.filter((c) => c.type === 'card');
}

// --- Question generation helpers ---

function shuffleArray(arr) {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

function makeBlankFromVerse(verse) {
    if (!verse) return { textWithBlank: '', missingWord: '' };
    const words = verse.split(/(\s+)/); // keep spaces
    const realWords = words.filter((w) => /[A-Za-z]/.test(w));
    if (realWords.length === 0) {
        return { textWithBlank: verse, missingWord: '' };
    }
    const candidates = realWords.filter((w) => w.replace(/[^A-Za-z]/g, '').length >= 4);
    const targetWord = (candidates.length > 0 ? candidates : realWords)[
        Math.floor(Math.random() * (candidates.length > 0 ? candidates.length : realWords.length))
    ];
    const cleanTarget = targetWord.replace(/[^A-Za-z]/g, '');
    const textWithBlank = verse.replace(targetWord, '____');
    return { textWithBlank, missingWord: cleanTarget };
}


// Level 1: verse <-> reference (MC)
function buildLevel1Questions(cards) {
    const questions = [];

    // --- Verse -> Reference (MC) ---
    // Uses card.fakeVerse as an optional *fake reference* (e.g., "1 Kings 20:15-23")
    cards.forEach((card) => {
        const correctRef = card.reference || '';
        if (!correctRef) return; // skip cards without a reference

        const fakeRefRaw = card.fakeVerse || '';
        const useFakeRef = fakeRefRaw && fakeRefRaw !== correctRef;
        const fakeRef = useFakeRef ? fakeRefRaw : null;

        // Collect other real references from sibling cards, excluding the correct and fake ref
        const otherRefsSet = new Set(
            cards
                .filter((c) => c !== card)
                .map((c) => c.reference || '')
                .filter((r) => r && r !== correctRef && r !== fakeRef)
        );
        const otherRefs = Array.from(otherRefsSet);

        const optionPool = [];
        if (fakeRef) {
            optionPool.push(fakeRef);
        }
        // Fill remaining wrong options from other real references (up to 3 total wrongs)
        optionPool.push(...shuffleArray(otherRefs).slice(0, 3 - optionPool.length));
        // Always include the correct reference
        optionPool.push(correctRef);

        const options = shuffleArray(optionPool);
        const correctIndex = options.findIndex((o) => o === correctRef);

        questions.push({
            type: 'mc',
            prompt: card.verse || '',
            options,
            correctIndex,
            description: 'Choose the correct reference for this verse.',
        });
    });

    // --- Reference -> Verse (MC) ---
    // NO fakeVerse here. Only real verses from cards.
    cards.forEach((card) => {
        const correctVerse = card.verse || '';
        if (!correctVerse) return; // skip cards without a verse

        // Collect other real verses from sibling cards, excluding the correct verse
        const otherVersesSet = new Set(
            cards
                .filter((c) => c !== card)
                .map((c) => c.verse || '')
                .filter((v) => v && v !== correctVerse)
        );
        const otherVerses = Array.from(otherVersesSet);

        const optionPool = shuffleArray(otherVerses).slice(0, 3);
        optionPool.push(correctVerse);

        const options = shuffleArray(optionPool);
        const correctIndex = options.findIndex((o) => o === correctVerse);

        questions.push({
            type: 'mc',
            prompt: card.reference || '',
            options,
            correctIndex,
            description: 'Choose the correct verse for this reference.',
        });
    });

    return shuffleArray(questions);
}

// Level 2: reference <-> paraphrase (MC)
function buildLevel2Questions(cards) {
    const gists = cards.map((c) => c.paraphrase || '');
    const questions = [];

    cards.forEach((card) => {
        const correctGist = card.paraphrase || '';
        const otherGists = gists.filter((g) => g !== correctGist);
        const optionPool = shuffleArray(otherGists).slice(0, 3);
        optionPool.push(correctGist);
        const options = shuffleArray(optionPool);
        questions.push({
            type: 'mc',
            prompt: card.reference || '',
            options,
            correctIndex: options.findIndex((o) => o === correctGist),
            description: 'Choose the correct gist/paraphrase.',
        });
    });

    cards.forEach((card) => {
        const correctRef = card.reference || '';
        const otherRefs = cards.filter((c) => c !== card).map((c) => c.reference || '');
        const optionPool = shuffleArray(otherRefs).slice(0, 3);
        optionPool.push(correctRef);
        const options = shuffleArray(optionPool);
        questions.push({
            type: 'mc',
            prompt: card.paraphrase || '',
            options,
            correctIndex: options.findIndex((o) => o === correctRef),
            description: 'Choose the correct reference for this gist.',
        });
    });

    return shuffleArray(questions);
}

// Level 3: fill-in-the-blank verse (now 2 questions per card)
function buildLevel4Questions(cards) {
    // Generate TWO fill-in-the-blank questions per card
    const questions = [];

    cards.forEach((card) => {
        const verse = card.verse || '';
        if (!verse) return;

        const first = makeBlankFromVerse(verse);
        const second = makeBlankFromVerse(verse);

        questions.push({
            type: 'blank',
            prompt: first.textWithBlank,
            answer: first.missingWord,
            description: 'Fill in the missing word from the verse.',
        });

        questions.push({
            type: 'blank',
            prompt: second.textWithBlank,
            answer: second.missingWord,
            description: 'Fill in the missing word from the verse.',
        });
    });

    return shuffleArray(questions);
}

// Level 4: free response reference typing (gist -> reference)
function buildLevel3Questions(cards) {
    return shuffleArray(
        cards.map((card) => ({
            type: 'free',
            prompt: card.paraphrase || '',
            answer: card.reference || '',
            description: 'Type the book, chapter, and verse.',
        }))
    );
}

function buildQuestionsForLevel(cards, level) {
    switch (level) {
        case 1:
            return buildLevel1Questions(cards);
        case 2:
            return buildLevel2Questions(cards);
        case 3:
            // Now: Level 3 = fill-in-the-blank verse (2 questions per card)
            return buildLevel4Questions(cards);
        case 4:
            // Now: Level 4 = free response reference typing
            return buildLevel3Questions(cards);
        default:
            return [];
    }
}

function normalizeRef(str) {
    if (!str) return '';
    return str.replace(/\s+/g, '').toLowerCase();
}

function normalizeWord(str) {
    if (!str) return '';
    return str.replace(/[^a-zA-Z]/g, '').toLowerCase();
}

// --- Main component ---

function Game() {
    const db = getFirestore();

    const [tree, setTree] = useState(null);
    const [folderStack, setFolderStack] = useState(['root']);

    const [userId, setUserId] = useState(null); // will hold auth.uid
    const [userEmail, setUserEmail] = useState(null);
    const [userProgress, setUserProgress] = useState(null);
    const [loadingProgress, setLoadingProgress] = useState(true);

    const [mode, setMode] = useState('browse'); // 'browse' | 'playing'
    const [activeFolderId, setActiveFolderId] = useState(null);
    const [currentLevel, setCurrentLevel] = useState(1);
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [numCorrect, setNumCorrect] = useState(0);
    const [numTotal, setNumTotal] = useState(0);
    const [finishedLevel, setFinishedLevel] = useState(false);
    const [lastResult, setLastResult] = useState(null); // 'correct' | 'incorrect' | null
    const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
    const [freeAnswer, setFreeAnswer] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // --- Auth & progress loading ---

    useEffect(() => {
        const auth = getAuth();
        const unsub = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserId(user.uid); // use uid as Firestore doc id
                setUserEmail(user.email || null);
            } else {
                setUserId(null);
                setUserEmail(null);
                setUserProgress(null);
            }
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        const loadProgress = async () => {
            if (!userId) {
                setLoadingProgress(false);
                return;
            }
            setLoadingProgress(true);
            try {
                const snap = await getDoc(doc(db, 'gameProgress', userId));
                if (snap.exists()) {
                    setUserProgress(snap.data());
                } else {
                    setUserProgress({ points: 0, stars: 0, folders: {} });
                }
            } catch (err) {
                console.error('Error loading game progress', err);
            } finally {
                setLoadingProgress(false);
            }
        };

        loadProgress();
    }, [db, userId]);

    // Ensure there is a per-user doc for the game (for admin / analytics)
    useEffect(() => {
        if (!userId) return;

        const ensureUserDoc = async () => {
            try {
                const userRef = doc(db, 'gameUsers', userId);
                const snap = await getDoc(userRef);

                if (!snap.exists()) {
                    await setDoc(userRef, {
                        email: userEmail || null,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                    });
                } else {
                    // keep email in sync and bump updatedAt
                    const data = snap.data() || {};
                    const updatePayload = {};
                    if (userEmail && data.email !== userEmail) {
                        updatePayload.email = userEmail;
                    }
                    updatePayload.updatedAt = serverTimestamp();

                    if (Object.keys(updatePayload).length > 0) {
                        await setDoc(userRef, updatePayload, { merge: true });
                    }
                }
            } catch (err) {
                console.error('Error ensuring game user doc', err);
            }
        };

        ensureUserDoc();
    }, [db, userId, userEmail]);

    // --- Load decks tree ---

    useEffect(() => {
        const loadTree = async () => {
            try {
                const snap = await getDoc(doc(db, 'gameMeta', 'decksTree'));
                if (snap.exists()) {
                    const data = snap.data();
                    if (data && data.tree) {
                        setTree(data.tree);
                        if (!data.tree.id) {
                            data.tree.id = 'root';
                        }
                    }
                } else {
                    // If no tree, initialize a basic root
                    setTree({ id: 'root', type: 'folder', title: 'Game Decks', category: null, children: [] });
                }
            } catch (err) {
                console.error('Error loading game tree', err);
            }
        };

        loadTree();
    }, [db]);

    const currentFolderId = folderStack[folderStack.length - 1];
    const currentFolder = useMemo(
        () => (tree ? findFolderById(tree, currentFolderId) || tree : null),
        [tree, currentFolderId]
    );

    const childFolders = useMemo(() => getChildFolders(currentFolder), [currentFolder]);
    const childCards = useMemo(() => getChildCards(currentFolder), [currentFolder]);

    const folderProgress = (folderId) => {
        if (!userProgress || !userProgress.folders) return null;
        return userProgress.folders[folderId] || null;
    };

    const computeFolderPercent = (folderId) => {
        const prog = folderProgress(folderId);
        if (prog) {
            if (prog.mastered) return 100;
            const levels = prog.levels || {};
            const masteredCount = Object.values(levels).filter((l) => l && l.mastered).length;
            if (masteredCount === 0) return 0;
            return Math.round((masteredCount / LEVEL_COUNT) * 100);
        }

        // If there is no direct progress for this folder, try aggregating from its child folders
        if (!tree) return 0;
        const folder = findFolderById(tree, folderId);
        if (!folder) return 0;
        const children = getChildFolders(folder);
        if (!children || children.length === 0) return 0;

        let totalPercent = 0;
        let counted = 0;
        children.forEach((child) => {
            const childPercent = computeFolderPercent(child.id);
            totalPercent += childPercent;
            counted += 1;
        });

        if (counted === 0) return 0;
        return Math.round(totalPercent / counted);
    };

    const computeFolderStars = (folderId) => {
        const prog = folderProgress(folderId);
        if (prog) {
            return prog.stars || 0;
        }

        // Aggregate stars from child folders if this folder has no direct progress
        if (!tree) return 0;
        const folder = findFolderById(tree, folderId);
        if (!folder) return 0;
        const children = getChildFolders(folder);
        if (!children || children.length === 0) return 0;

        let totalStars = 0;
        children.forEach((child) => {
            totalStars += computeFolderStars(child.id);
        });

        return totalStars;
    };

    const updateProgressInFirestore = async (folderId, level, correct, total, isCorrect, isFinalQuestion) => {
        if (!userId) return; // allow playing locally without saving (keyed by auth.uid)

        setUserProgress((prev) => {
            const accuracy = total ? correct / total : 0;
            const requiredCorrect = Math.min(10, total || 0);
            const masteryEligible =
                requiredCorrect > 0 && correct >= requiredCorrect && accuracy >= 0.8;
            const pointsDelta = isCorrect ? 2 : -1; // per-question delta

            console.log('Updating progress', {
                folderId,
                level,
                correct,
                total,
                accuracy,
                masteryEligible,
                isFinalQuestion,
                pointsDelta,
            });

            const base = prev || { points: 0, stars: 0, folders: {} };
            const next = JSON.parse(JSON.stringify(base));

            // Always update points per question
            next.points = (next.points || 0) + pointsDelta;

            if (!next.folders) next.folders = {};
            if (!next.folders[folderId]) {
                next.folders[folderId] = { levels: {}, mastered: false, stars: 0 };
            }

            const folderProg = next.folders[folderId];
            if (!folderProg.levels) folderProg.levels = {};

            const previousLevel = folderProg.levels[level] || { mastered: false, bestAccuracy: 0 };
            const previouslyMastered = !!previousLevel.mastered;

            if (isFinalQuestion) {
                // Only on the final question of a run do we update mastery / stars
                if (masteryEligible) {
                    folderProg.levels[level] = {
                        mastered: true,
                        bestAccuracy: Math.max(previousLevel.bestAccuracy || 0, accuracy),
                    };
                    if (!previouslyMastered) {
                        folderProg.stars = (folderProg.stars || 0) + 1;
                        next.stars = (next.stars || 0) + 1;
                    }
                } else {
                    folderProg.levels[level] = {
                        mastered: previouslyMastered,
                        bestAccuracy: Math.max(previousLevel.bestAccuracy || 0, accuracy),
                    };
                }

                const allMastered = Array.from({ length: LEVEL_COUNT }, (_, i) => i + 1).every(
                    (lvl) => folderProg.levels[lvl] && folderProg.levels[lvl].mastered
                );

                if (allMastered && !folderProg.mastered) {
                    folderProg.mastered = true;
                    folderProg.stars = (folderProg.stars || 0) + 3;
                    next.stars = (next.stars || 0) + 3;
                }
            } else {
                // Mid-level: optionally update bestAccuracy but do NOT change mastery or stars
                folderProg.levels[level] = {
                    mastered: previouslyMastered,
                    bestAccuracy: Math.max(previousLevel.bestAccuracy || 0, accuracy),
                };
            }

            // Attach email (if we have it) for easier admin lookup
            if (userEmail) {
                next.email = userEmail;
            }

            // Persist progress (keyed by auth.uid) in gameProgress
            setDoc(doc(db, 'gameProgress', userId), next, { merge: false }).catch((err) => {
                console.error('Error saving progress', err);
            });

            // Also mirror summary into gameUsers for easier querying
            const userSummary = {
                email: userEmail || null,
                points: next.points || 0,
                stars: next.stars || 0,
                folders: next.folders || {},
                updatedAt: serverTimestamp(),
            };

            setDoc(doc(db, 'gameUsers', userId), userSummary, { merge: true }).catch((err) => {
                console.error('Error saving user summary', err);
            });

            return next;
        });
    };

    const handleOpenFolder = (folderId) => {
        const folder = tree ? findFolderById(tree, folderId) : null;
        if (!folder) return;

        const folders = getChildFolders(folder);
        const cards = getChildCards(folder);

        if (folders.length > 0) {
            // navigate deeper
            setFolderStack((prev) => [...prev, folderId]);
            setMode('browse');
            return;
        }

        if (cards.length > 0) {
            // leaf folder: start game
            const prog = folderProgress(folderId);
            let nextLevel = 1;
            if (prog && prog.levels) {
                for (let lvl = 1; lvl <= LEVEL_COUNT; lvl += 1) {
                    if (!prog.levels[lvl] || !prog.levels[lvl].mastered) {
                        nextLevel = lvl;
                        break;
                    }
                }
            }

            const qs = buildQuestionsForLevel(cards, nextLevel);

            setActiveFolderId(folderId);
            setCurrentLevel(nextLevel);
            setQuestions(qs);
            setCurrentIndex(0);
            setNumCorrect(0);
            setNumTotal(0);
            setFinishedLevel(false);
            setLastResult(null);
            setSelectedOptionIndex(null);
            setFreeAnswer('');
            setMode('playing');
        }
    };

    const handleBack = () => {
        if (mode === 'playing') {
            setMode('browse');
            setActiveFolderId(null);
            return;
        }
        setFolderStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    };

    const currentQuestion = mode === 'playing' && questions.length > 0 ? questions[currentIndex] : null;

    const handleAnswerMC = (optionIndex) => {
        if (!currentQuestion || finishedLevel || submitting) return;
        setSubmitting(true);

        const isCorrect = optionIndex === currentQuestion.correctIndex;
        setSelectedOptionIndex(optionIndex);
        setLastResult(isCorrect ? 'correct' : 'incorrect');

        setTimeout(() => {
            setSubmitting(false);
            setSelectedOptionIndex(null);
            setLastResult(null);

            setNumTotal((prevTotal) => {
                const newTotal = prevTotal + 1;
                setNumCorrect((prevCorrect) => {
                    const newCorrect = isCorrect ? prevCorrect + 1 : prevCorrect;
                    const isLastQuestion = currentIndex + 1 >= questions.length;

                    // Save progress after every question using the current run stats
                    updateProgressInFirestore(
                        activeFolderId,
                        currentLevel,
                        newCorrect,
                        newTotal,
                        isCorrect,
                        isLastQuestion
                    );

                    if (isLastQuestion) {
                        setFinishedLevel(true);
                    } else {
                        setCurrentIndex((prev) => prev + 1);
                    }

                    return newCorrect;
                });
                return newTotal;
            });
        }, 1500);
    };

    const handleSubmitFreeOrBlank = (e) => {
        e.preventDefault();
        if (!currentQuestion || finishedLevel || submitting) return;
        setSubmitting(true);

        let isCorrect = false;
        if (currentQuestion.type === 'free') {
            isCorrect = normalizeRef(freeAnswer) === normalizeRef(currentQuestion.answer);
        } else if (currentQuestion.type === 'blank') {
            isCorrect = normalizeWord(freeAnswer) === normalizeWord(currentQuestion.answer);
        }

        setLastResult(isCorrect ? 'correct' : 'incorrect');

        setTimeout(() => {
            setSubmitting(false);
            setFreeAnswer('');
            setLastResult(null);

            setNumTotal((prevTotal) => {
                const newTotal = prevTotal + 1;
                setNumCorrect((prevCorrect) => {
                    const newCorrect = isCorrect ? prevCorrect + 1 : prevCorrect;
                    const isLastQuestion = currentIndex + 1 >= questions.length;

                    // Save progress after every question
                    updateProgressInFirestore(
                        activeFolderId,
                        currentLevel,
                        newCorrect,
                        newTotal,
                        isCorrect,
                        isLastQuestion
                    );

                    if (isLastQuestion) {
                        setFinishedLevel(true);
                    } else {
                        setCurrentIndex((prev) => prev + 1);
                    }

                    return newCorrect;
                });
                return newTotal;
            });
        }, 1500);
    };

    const handleNextLevelOrRetry = () => {
        if (!tree || !activeFolderId) {
            setMode('browse');
            return;
        }

        const folder = findFolderById(tree, activeFolderId);
        const cards = getChildCards(folder);
        const accuracy = numTotal ? numCorrect / numTotal : 0;
        const requiredCorrect = Math.min(10, numTotal);
        const masteryEligible = requiredCorrect > 0 && numCorrect >= requiredCorrect && accuracy >= 0.8;

        if (!masteryEligible) {
            // retry same level
            const qs = buildQuestionsForLevel(cards, currentLevel);
            setQuestions(qs);
            setCurrentIndex(0);
            setNumCorrect(0);
            setNumTotal(0);
            setFinishedLevel(false);
            setLastResult(null);
            setSelectedOptionIndex(null);
            setFreeAnswer('');
            return;
        }

        if (currentLevel < LEVEL_COUNT) {
            // advance to the next level
            const nextLevel = currentLevel + 1;
            const qs = buildQuestionsForLevel(cards, nextLevel);
            setCurrentLevel(nextLevel);
            setQuestions(qs);
            setCurrentIndex(0);
            setNumCorrect(0);
            setNumTotal(0);
            setFinishedLevel(false);
            setLastResult(null);
            setSelectedOptionIndex(null);
            setFreeAnswer('');
        } else {
            // all levels done for this folder
            setMode('browse');
            setActiveFolderId(null);
        }
    };

    const breadcrumb = useMemo(() => {
        if (!tree) return 'Loading...';
        return folderStack
            .map((id) => {
                const folder = findFolderById(tree, id);
                return folder ? folder.title : 'Folder';
            })
            .join(' / ');
    }, [tree, folderStack]);

    const activeFolder = activeFolderId && tree ? findFolderById(tree, activeFolderId) : null;

    return (
        <div className="game-play-container">
            <div className="game-play-header">
                <div className="game-play-breadcrumb">
                    <button type="button" className="game-edit-back-button" onClick={handleBack}>
                        ← Back
                    </button>
                    <span className="game-edit-breadcrumb-text">{breadcrumb}</span>
                </div>
                <div className="game-play-meta">
                    {userProgress && (
                        <span className="game-play-meta-item">
                            Points: {userProgress.points || 0} | Stars: {userProgress.stars || 0}
                        </span>
                    )}
                </div>
            </div>

            {mode === 'browse' && (
                <div className="game-edit-grid">
                    {childFolders && childFolders.length > 0 ? (
                        childFolders.map((folder) => {
                            const percent = computeFolderPercent(folder.id);
                            const stars = computeFolderStars(folder.id);
                            return (
                                <div
                                    key={folder.id}
                                    className="game-edit-item game-edit-item-folder game-play-folder-card"
                                    onClick={() => handleOpenFolder(folder.id)}
                                >
                                    <div className="game-edit-item-header">
                                        <span className="game-edit-item-label">Folder</span>
                                        {stars > 0 && <span className="game-play-stars">★ {stars}</span>}
                                    </div>
                                    <div className="game-edit-item-title">{folder.title}</div>
                                    <div className="game-edit-item-meta">
                                        <div className="game-play-progress-bar">
                                            <div
                                                className="game-play-progress-fill"
                                                style={{ width: `${percent}%` }}
                                            />
                                        </div>
                                        <div className="game-play-progress-text">{percent}% mastered</div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="game-edit-empty">No topics available here yet.</div>
                    )}
                </div>
            )}

            {mode === 'playing' && currentQuestion && activeFolder && (
                <div className="game-play-session">
                    <div className="game-play-session-header">
                        <h2 className="game-play-session-title">{activeFolder.title}</h2>
                        <div className="game-play-session-subtitle">
                            Level {currentLevel} of {LEVEL_COUNT} · Question {currentIndex + 1} of {questions.length}
                        </div>
                        <div className="game-play-session-subtitle">{currentQuestion.description}</div>
                        <div className="game-play-session-stats">
                            Correct: {numCorrect} / {numTotal}
                        </div>
                    </div>

                    {currentQuestion.type === 'mc' && (
                        <div className="game-play-question-block">
                            <div className="game-play-question-prompt">{currentQuestion.prompt}</div>
                            <div className="game-play-options">
                                {currentQuestion.options.map((opt, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className={
                                            'game-play-option-button' +
                                            (selectedOptionIndex === idx
                                                ? idx === currentQuestion.correctIndex
                                                    ? ' correct'
                                                    : ' incorrect'
                                                : '')
                                        }
                                        onClick={() => handleAnswerMC(idx)}
                                        disabled={submitting}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                            {lastResult && (
                                <div
                                    className={
                                        'game-play-feedback' +
                                        (lastResult === 'correct' ? ' correct' : ' incorrect')
                                    }
                                >
                                    {lastResult === 'correct'
                                        ? 'Correct!'
                                        : `Incorrect. Correct answer: ${currentQuestion.options[currentQuestion.correctIndex]
                                        }`}
                                </div>
                            )}
                        </div>
                    )}

                    {(currentQuestion.type === 'free' || currentQuestion.type === 'blank') && (
                        <form className="game-play-question-block" onSubmit={handleSubmitFreeOrBlank}>
                            <div className="game-play-question-prompt">{currentQuestion.prompt}</div>
                            <input
                                type="text"
                                className="game-edit-input game-play-free-input"
                                value={freeAnswer}
                                onChange={(e) => setFreeAnswer(e.target.value)}
                                placeholder={
                                    currentQuestion.type === 'free'
                                        ? 'Type the reference (e.g., 1 Tim 3:15)'
                                        : 'Type the missing word'
                                }
                                disabled={submitting}
                            />
                            <button type="submit" className="game-edit-button primary" disabled={submitting}>
                                Submit
                            </button>
                            {lastResult && (
                                <div
                                    className={
                                        'game-play-feedback' +
                                        (lastResult === 'correct' ? ' correct' : ' incorrect')
                                    }
                                >
                                    {lastResult === 'correct'
                                        ? 'Correct!'
                                        : `Incorrect. Correct answer: ${currentQuestion.answer}`}
                                </div>
                            )}
                        </form>
                    )}

                    {finishedLevel && (
                        <div className="game-play-level-summary">
                            <div>
                                Level complete! Accuracy: {numTotal ? Math.round((numCorrect / numTotal) * 100) : 0}%
                            </div>
                            <div className="game-play-summary-actions">
                                <button
                                    type="button"
                                    className="game-edit-button secondary"
                                    onClick={handleNextLevelOrRetry}
                                >
                                    {(() => {
                                        const requiredCorrect = Math.min(10, numTotal);
                                        const accuracy = numTotal ? numCorrect / numTotal : 0;
                                        const canAdvance = requiredCorrect > 0 && numCorrect >= requiredCorrect && accuracy >= 0.8;
                                        if (!canAdvance) return 'Retry Level';
                                        return currentLevel < LEVEL_COUNT ? 'Next Level' : 'Back to Topics';
                                    })()}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default Game;
